import { MonoclineCategoryProps, MonoclineDataProps, useCommonComponents, useLoadingOrError } from "@me8eon/components";
import { AISession, AiSessionData, AiSessionTc, MessageWithActionAndData } from "./messages.sessions";
import React, { FC, useMemo } from "react";
import { useLanguage } from "@me8eon/language";
import { useTranslation } from "@me8eon/translation";
import { useTime, isDateWithinGivenDays } from "@me8eon/time";
import { normalizeLocaleForIntl } from "@me8eon/time";
import { reverse } from "@me8eon/arrays";

import { useTheme } from "@me8eon/themes";
import { Icon, ThemedIcon } from "@me8eon/icons";
import { EonUiTooltip } from "@eon-ui/eon-ui-components-react";
import "./sessions.and.messages.scss";
import { useAiSessionIdOps, useAsyncSessionAndMessages, useSessionAndMessageOps } from "./session.messages.store";
import { ParentAndChildrenWithIds } from "@me8eon/async_storage";
import { isDataAs } from "@me8eon/async";
import { useNavigate } from "react-router";
import { useWindowProperties } from "me-at-eon/src/context/WindowContext";

export const SessionsHeader: FC<MonoclineCategoryProps<AISession>> = ({ category, index, selected, onClick, isMobile }) => {
    const { monoClineStyles } = useTheme();
    const { header, selectedHeader } = monoClineStyles;
    const style = selected ? selectedHeader : header;
    return <div data-testid={`session.header.${index}`} data-selected={Boolean(selected)} style={{ ...style, fontWeight: "bold", ...(isMobile ? { paddingLeft: "8px" } : { paddingLeft: "14px" }) }} onClick={() => onClick?.(category, index)}>{category}</div>;
};

export const SessionsData: FC<MonoclineDataProps<AISession>> = ({ category, item, index, selected, onClick, isMobile }) => {
    const { monoClineStyles } = useTheme();
    const { item: itemStyle, selectedItem } = monoClineStyles;
    const style = selected ? selectedItem : itemStyle;
    const { deleteChild: deleteSession } = useSessionAndMessageOps();
    const translate = useTranslation();

    const selectMsg = () => {
        document.getElementById("sidebar-backdrop")?.click();
        onClick?.(item, index);
    }
    return (
        <div
            data-testid={`session.data.${category}.${index}`}
            data-selected={Boolean(selected)}
            style={{ ...style, fontWeight: Boolean(selected) ? "600" : "" }}
            className="item-container"
        >
            <button style={{ width: "100%", border: "0", background: "transparent", textAlign: "left", wordBreak: "break-word", ...(isMobile ? { padding: "0.5rem 8px" } : { padding: "0.5rem 14px" }) }} data-testid={`session.data.${category}.${index}-span`} onClick={selectMsg}> {item.name} </button>
            <button className={`item-delete ${isMobile ? "" : "item-delete-web"}`} data-testid={`delete-${category}-${index}`} onClick={() => deleteSession(item.id)} title={translate("chatBot.sessions.deleteMessage")}>
                <Icon name="trash_bin_small" />
            </button>
        </div>
    );
};

const NoChatComponent = ({ onClose }: { onClose?: () => void }) => {
    const translate = useTranslation();
    const { recentChatsLayout } = useTheme();
    const { noChatsContainer, noChatsMessage, newChat } = recentChatsLayout;
    return (
        <div data-testid="nochat" style={onClose ? noChatsContainer : { marginTop: "32px" }}>
            <div style={noChatsMessage}>
                <span>
                    <ThemedIcon name="clock_recent" />
                </span>
                <span>{translate("chatBot.sessions.noRecentChat")}</span>
            </div>
            {onClose && (<div style={newChat} onClick={onClose}>
                <span>
                    <ThemedIcon name="add" />
                </span>{" "}
                <span>{translate("chatBot.sessions.startNewChat")}</span>
            </div>)}
        </div>
    );
};

export type SessionsAndMessagesProps = {
    isOpen: boolean;
    onClose: () => void;
}

export const RecentChat = ({ onClose }: { onClose?: () => void }) => {
    const LoadingOrError = useLoadingOrError<ParentAndChildrenWithIds<string, AiSessionData, string, MessageWithActionAndData[]>>();
    const { recentChatsLayout } = useTheme();
    const [state] = useAsyncSessionAndMessages();
    const translate = useTranslation();
    const {
        recentChatsList,
    } = recentChatsLayout;
    return (
        <div style={{ ...recentChatsList, ...(onClose ? { border: '1px solid #E5E5EA' } : {}) }}>
            <LoadingOrError
                state={state}
                whatWeAreLoading={translate("chatBot.sessions.title")}
                rootId="sessions-and-messages"
            >
                {(data) => {
                    function hasSession() {
                        if (data.parent.sessions.length > 1) return true;
                        return !!(data.parent.sessions.length === 1 && data.parent.sessions[0].name);
                    }

                    return hasSession() ? <InvertedMonoCline data={data.parent} onClose={onClose} /> : <NoChatComponent onClose={onClose} />;
                }}

            </LoadingOrError>
        </div>
    )
}

export function SessionsAndMessages({
    isOpen,
    onClose,
}: SessionsAndMessagesProps) {
    // const LoadingOrError = useLoadingOrError<ParentAndChildrenWithIds<string, AiSessionData, string, MessageWithActionAndData[]>>();
    const [state] = useAsyncSessionAndMessages();
    const { deleteChild } = useSessionAndMessageOps()
    // const { deleteAllSessions } = useSessionAndMessageOps();
    const translate = useTranslation();
    const { recentChatsLayout } = useTheme();
    const {
        recentChats,
        recentChatsHeading,
        recentChatsHeadingTitle,
        recentChatsHeadingClear,
        recentChatsFooter,
    } = recentChatsLayout;
    if (!isOpen) return <></>;
    function deleteAllSessions() {
        if (!isDataAs(state)) return;
        const ids = state?.data?.parent.sessions.map(session => session.id) ?? [];
        deleteChild(...ids)
    }
    return (
        <div data-testid="ai.sessions.container" style={recentChats}>
            <div style={recentChatsHeading}>
                <div style={recentChatsHeadingTitle}>
                    <ThemedIcon name="clock_recent" />
                    {translate("chatBot.sessions.title")}
                </div>
                <div style={recentChatsHeadingClear}>
                    <span data-testid='delete.all.sessions' style={recentChatsHeadingClear} onClick={deleteAllSessions}>
                        <Icon name="trash_bin_small" />
                        {translate("chatBot.sessions.clearAll")}
                    </span>
                    <span data-testid='sessions.and.messages.close' onClick={onClose}>
                        <Icon name="close" />
                    </span>
                </div>
            </div>
            <RecentChat onClose={onClose} />
            <div style={recentChatsFooter}>
                {translate("chatBot.sessions.footer")} {" "}
                <span>
                    <a href="#" onClick={(e) => e.preventDefault()} style={recentChatsFooter}>{translate("chatBot.sessions.learnMore")}</a>
                    <EonUiTooltip
                        visibility="onClick"
                        placement="top"
                    >
                        <p style={recentChatsFooter}>{translate("chatBot.sessions.learnMoreTooltip")}</p>
                    </EonUiTooltip>
                </span>
            </div>
        </div>
    );
}

export function InvertedMonoCline({ data, onClose }: { data: AiSessionData, onClose?: () => void }) {
    const { MonoCline } = useCommonComponents();
    // const  sessionIdOps  = useSessionAndMessages();
    const navigate = useNavigate();
    const translate = useTranslation();
    const [sessionId, setSessionId] = useAiSessionIdOps();
    const { recentChatsLayout } = useTheme();
    const {
        recentChatsFooter,
    } = recentChatsLayout;
    const timeService = useTime();
    const [language] = useLanguage();
    const relativeDayLabels = useMemo(() => {
        const formatter = new Intl.RelativeTimeFormat(normalizeLocaleForIntl(language), { numeric: "auto" });
        return {
            today: formatter.format(0, "day"),
            yesterday: formatter.format(-1, "day"),
        };
    }, [language]);
    const { screenWidth } = useWindowProperties();
    const isMobile = screenWidth < 768;
    const items = useMemo(() => reverse(data.sessions.filter(session => session.name && isDateWithinGivenDays(session.time, 30))), [data.sessions]);
    const selectedItem = useMemo(() => data.sessions.find(item => item.id === sessionId), [data.sessions, sessionId]);
    const onItemClick = (item: AISession) => {
        navigate("/ai");
        setSessionId(item.id);
        onClose?.();
    };

    const disclaimer = <div>
        <span>{translate("chatBot.sessions.footer")} &nbsp;</span>
        <span>
            <a href="#" onClick={(e) => e.preventDefault()} style={recentChatsFooter}>{translate("chatBot.sessions.learnMore")}</a>
            <EonUiTooltip
                visibility="onClick"
                placement="top"
            >
                <p style={recentChatsFooter}>{translate("chatBot.sessions.learnMoreTooltip")}</p>
            </EonUiTooltip>
        </span>
    </div>

    return <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
        <MonoCline
            disclaimer={disclaimer}
            items={items}
            isMobile={isMobile}
            typeClass={AiSessionTc(timeService, language, relativeDayLabels)}
            Header={SessionsHeader}
            Data={SessionsData}
            onDataClick={onItemClick}
            selectedItem={selectedItem}
        />
    </div>;

}
